import { SettingsManager } from "@viamap/viamap2-common";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { fetchServitutProcessor } from "src/utils/servitutProcessorServiceHost";
import { getPLAPipeFunctionSignatures, validatePLAExpression } from "./PLAExpression";
import { PLAElementRecord, PLAProject, PLARelationRecord } from "./PLAState";
import localization from "./localization.json";

export type PLAExpressionAssistRequest = {
   description: string;
   language: string;
   grammar: string;
   currentElementTypeId: string;
   elementTypes: { id: string; name: string; attributes: { id: string; name: string; type: string; valueType?: string; cubeDimensions?: string[]; description?: string }[] }[];
   relationTypes: { id: string; name: string; connects: { from: string; to: string }[] }[];
   currentExpression?: string;
   previousAttempt?: { expression: string; error: string };
};

export type PLAExpressionAssistResult = {
   expression: string;
   explanation: string;
   valid: boolean;
   error?: string;
   attempts: number;
};

export type PLAExpressionAssistCaller = { userRef?: string; userName?: string; organizationId?: string };

export const PLA_EXPRESSION_ASSIST_MAX_ATTEMPTS = 3;

/** English syntax help and examples, plus the list of pipe operations, as the grammar for the model. */
export function buildPLAExpressionGrammar(): string {
   const en = localization.en as Record<string, string>;
   const pick = (prefix: string) => Object.keys(en).filter((key) => key.startsWith(prefix)).map((key) => `- ${en[key]}`);
   return [
      "Simple grammar:", en["Expression syntax grammar notation"], en["Expression syntax grammar"],
      "Syntax:", ...pick("Expression syntax hint "),
      "", "Pipe operations (argument count):", getPLAPipeFunctionSignatures().join(", "),
      "", "Examples:", ...pick("Expression syntax example "),
   ].join("\n");
}

/** Names and types only; element values are never sent. */
export function buildPLAExpressionAssistSchema(project: PLAProject, elements: PLAElementRecord[] = [], relations: PLARelationRecord[] = []): Pick<PLAExpressionAssistRequest, "elementTypes" | "relationTypes"> {
   const typeName = (id: string) => project.elementTypes[id]?.name || id;
   const elementTypes = Object.entries(project.elementTypes)
      .sort(([, a], [, b]) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(([id, type]) => ({
         id,
         name: type.name || id,
         attributes: Object.entries(type.attributes || {}).map(([attributeId, attribute]) => ({
            id: attributeId,
            name: attribute.name || attributeId,
            type: attribute.type,
            valueType: attribute.valueType,
            cubeDimensions: attribute.type === "cube" ? (attribute.cubeDimensions || []).map((dimension) => typeName(dimension.elementTypeId)) : undefined,
            description: attribute.description || undefined,
         })),
      }));
   const typeById = new Map(elements.map((element) => [element.id, element.elementType]));
   const connects = new Map<string, Map<string, { from: string; to: string }>>();
   for (const relation of relations) {
      const from = typeById.get(relation.fromId);
      const to = typeById.get(relation.toId);
      if (!from || !to) continue;
      const pairs = connects.get(relation.relationType) || new Map();
      pairs.set(`${from}\u001f${to}`, { from: typeName(from), to: typeName(to) });
      connects.set(relation.relationType, pairs);
   }
   const relationTypes = Object.entries(project.relationTypes || {}).map(([id, relation]) => ({
      id,
      name: relation.name || id,
      connects: [...(connects.get(id)?.values() || [])],
   }));
   return { elementTypes, relationTypes };
}

async function readError(response: Response): Promise<string> {
   const text = await response.text().catch(() => "");
   try {
      const payload = JSON.parse(text) as { detail?: string };
      if (payload.detail) return payload.detail;
   } catch {
      // Not JSON; fall back to the raw body.
   }
   return text || `${response.status} ${response.statusText}`;
}

/**
 * Asks the LLM backend for an expression and validates it locally.
 * Rejected expressions are sent back with the validator message, up to PLA_EXPRESSION_ASSIST_MAX_ATTEMPTS calls in total.
 */
export async function requestPLAExpressionSuggestion(params: {
   description: string;
   language: string;
   project: PLAProject;
   elementTypeId: string;
   elements?: PLAElementRecord[];
   relations?: PLARelationRecord[];
   currentExpression?: string;
   caller?: PLAExpressionAssistCaller;
   signal?: AbortSignal;
   fetcher?: typeof fetchServitutProcessor;
}): Promise<PLAExpressionAssistResult> {
   const fetcher = params.fetcher || fetchServitutProcessor;
   const headers: Record<string, string> = { "Content-Type": "application/json", "access-token": String(SettingsManager.getSystemSetting("jobs.jobServiceAPIKey") || "") };
   if (params.caller?.userRef) headers["user-ref"] = params.caller.userRef;
   if (params.caller?.userName) headers["user-name"] = params.caller.userName;
   if (params.caller?.organizationId) headers["organization-id"] = params.caller.organizationId;
   const base: PLAExpressionAssistRequest = {
      description: params.description.trim(),
      language: params.language,
      grammar: buildPLAExpressionGrammar(),
      currentElementTypeId: params.elementTypeId,
      ...buildPLAExpressionAssistSchema(params.project, params.elements, params.relations),
      currentExpression: params.currentExpression?.trim() || undefined,
   };
   void ActionTracker.track("PlanIT", "ExpressionAssist", "Request");
   let previousAttempt: PLAExpressionAssistRequest["previousAttempt"];
   let last: PLAExpressionAssistResult = { expression: "", explanation: "", valid: false, attempts: 0 };
   for (let attempt = 1; attempt <= PLA_EXPRESSION_ASSIST_MAX_ATTEMPTS; attempt++) {
      const response = await fetcher("/planit/expression-assist", { method: "POST", headers, signal: params.signal, body: JSON.stringify({ ...base, previousAttempt }) });
      if (!response.ok) throw new Error(await readError(response));
      const payload = await response.json() as { expression?: string; explanation?: string };
      const expression = (payload.expression || "").trim();
      const explanation = (payload.explanation || "").trim();
      if (!expression) return { expression, explanation, valid: false, attempts: attempt };
      const validation = validatePLAExpression(expression, { project: params.project, elementTypeId: params.elementTypeId });
      last = { expression, explanation, valid: validation.valid, error: validation.error, attempts: attempt };
      if (validation.valid) return last;
      previousAttempt = { expression, error: validation.error || "Invalid expression" };
   }
   return last;
}
